import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Card,
    Col,
    Container,
    Form,
    Row,
    Spinner,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AutocompleteSelect from "../../../components/autocomplete/AutocompleteSelect/AutocompleteSelect";
import i18n from "../../../i18n";
import {
    createActionPermissions,
    deleteActionPermission,
    fetchActionPermission,
    fetchActionsPermissions,
    fetchActionsPermissionsLabels,
    updateActionPermission,
} from "../../../redux/slices/actionsPermissionsSlice";
import "./ActionsPermissionsCRUD.css";
import {
    getAssignmentInfo,
    groupActionLabelsByEntity,
    normalizeActionLabels,
    pickActionBooleanMap,
} from "../../../components/permissions/helpers/PermissionsHelper/PermissionsHelper";

const ActionsPermissionsCRUD = () => {

    console.log("ActionsPermissionsCRUD rendered");

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { users = null, roles = null} = state;

    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);

    const { id } = useParams();
    const isCreate = id === "new";

    const { item, items, labels, isLoading, error } = useSelector((state) => state.actions);

    const [assignmentType, setAssignmentType] = useState("user");
    const [_assignmentId, setAssignmentId] = useState("");
    const [replicateId, setReplicateId] = useState("");
    const [basePayload, setBasePayload] = useState({});
    const [actionState, setActionState] = useState({});

    useEffect(() => {
        dispatch(fetchActionsPermissionsLabels());
        dispatch(fetchActionsPermissions());
        if (!isCreate) {
            dispatch(fetchActionPermission(id));
        }
    }, [dispatch, id, isCreate]);

    const normalizedLabels = useMemo(
        () => normalizeActionLabels(labels),
        [labels],
    );

    const labelKeys = useMemo(
        () => normalizedLabels.map((label) => label.key),
        [normalizedLabels],
    );

    useEffect(() => {

        if (isCreate) {
            const defaultActions = Object.fromEntries(
                labelKeys.map((key) => [key, false]),
            );
            setActionState(defaultActions);
            setBasePayload({});
            return;
        }

        if (!item || String(item.id) !== String(id)) return;

        const bools = pickActionBooleanMap(item, labelKeys);
        const fallbackBools = Object.fromEntries(
            labelKeys.map((key) => [key, bools[key] || false]),
        );
        const merged = labelKeys.length > 0 ? fallbackBools : bools;

        setActionState(merged);
        setBasePayload(item);

        if (item.user_id && !item.role_id) {
            setAssignmentType("user");
            setAssignmentId(String(item.user_id));
        } else if (item.role_id && !item.user_id) {
            setAssignmentType("role");
            setAssignmentId(String(item.role_id));
        }

    }, [id, isCreate, item, labelKeys]);

    const editableActionLabels = useMemo(() => {
        if (normalizedLabels.length > 0) return normalizedLabels;
        return Object.keys(actionState).map((key) => ({
            key,
            title: key,
            description: "",
        }));
    }, [normalizedLabels, actionState]);

    const groupedActionLabels = useMemo(
        () => groupActionLabelsByEntity(editableActionLabels),
        [editableActionLabels],
    );

    const assignmentInfo = useMemo(
        () => getAssignmentInfo(basePayload),
        [basePayload],
    );

    const onReplicate = () => {

        if (!replicateId) return;
        const source = (Array.isArray(items) ? items : []).find(
            (entry) => String(entry.id) === String(replicateId),
        );
        if (!source) return;

        const sourceActions = pickActionBooleanMap(source, labelKeys);
        if (labelKeys.length > 0) {
            setActionState(
                Object.fromEntries(
                    labelKeys.map((key) => [key, Boolean(sourceActions[key])]),
                ),
            );
        } else {
            setActionState(sourceActions);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (isCreate) {
            const payload = {
                ...actionState,
                ...{ 
                    user_id: selectedUser?.value || null,
                    role_id: selectedRole?.value || null, 
                },
            };

            const result = await dispatch(createActionPermissions(payload));
            if (!result.error) {
                navigate("/organization/settings/permissions");
            }
            return;
        }

        const payload = {
            ...actionState,
            id,
        };

        const result = await dispatch(updateActionPermission(payload));
        if (!result.error) {
            navigate("/organization/settings/permissions");
        }
    };

    const onDelete = async () => {
        if (isCreate) return;
        const confirmed = window.confirm(
            i18n.t("pages.ManagePermissions.confirm_delete"),
        );
        if (!confirmed) return;

        const result = await dispatch(deleteActionPermission(id));
        if (!result.error) {
            navigate("/organization/settings/permissions");
        }
    };

    const onRowSelect = (rowData) => {
        if (assignmentType === "user") {
            setSelectedUser(rowData);
            setSelectedRole(null);
        }   
        else if (assignmentType === "role") {
            setSelectedRole(rowData);
            setSelectedUser(null);
        }
        setAssignmentId(rowData.value)
    }

    const onSwitchChange = (actionLabel) => (e) => {
        setActionState((prev) => ({
            ...prev,
            [actionLabel.key]: e.target.checked,
        }));
    }

    const permissionGroupChecked = (permGroup) => {
        const switchUncheked = (act) => Boolean(actionState[act.key]) === false;
        return !permGroup.actions.some(switchUncheked);
    }

    const onPermissionGroupSwitchChange = (permGroup) => (e) => {
        const checked = e.target.checked;
        const affectedActionsBooleans = Object.fromEntries(
            permGroup.actions.map((action) => [action.key, checked])
        );
        setActionState((prev) => ({
            ...prev,
            ...affectedActionsBooleans,
        }));
        // e.target.checked = e.target.checked;
    }

    return (
        <Container className="py-4 permissions-shell fade-in-up">
            <div className="permissions-toolbar mb-4 mt-5">
                <div>
                    <h2 className="permissions-title mb-1">
                        {isCreate
                            ? i18n.t("pages.ManagePermissions.create_actions_permissions")
                            : i18n.t("pages.ManagePermissions.edit_actions_permissions")}
                    </h2>
                    <p className="permissions-subtitle mb-0">
                        {i18n.t("pages.ManagePermissions.crud_subtitle")}
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/organization/settings/permissions")}
                >
                    {i18n.t("common.actions.go_back")}
                </button>
            </div>

            {error && (
                <Alert variant="danger" className="border-0 shadow-sm">
                    {typeof error === "string"
                        ? error
                        : i18n.t("pages.ManagePermissions.generic_error")}
                </Alert>
            )}

            <Form onSubmit={onSubmit}>
                <Card className="permissions-panel mb-3 border-0">
                    <Card.Body>
                        {isCreate ? (
                            <Row className="g-3">
                                <Col xs={12} md={4}>
                                    <Form.Label className="fw-bold txt-subtle">
                                        {i18n.t("pages.ManagePermissions.assignment_type")}
                                    </Form.Label>
                                    <Form.Select
                                        className="bg-surface txt-main shadow-none border-thin"
                                        value={assignmentType}
                                        onChange={(e) => setAssignmentType(e.target.value)}
                                    >
                                        <option value="user">
                                            {i18n.t("pages.ManagePermissions.filter_user")}
                                        </option>
                                        <option value="role">
                                            {i18n.t("pages.ManagePermissions.filter_role")}
                                        </option>
                                    </Form.Select>
                                </Col>
                                <Col xs={12} md={8}>
                                    {assignmentType === "user" ? (
                                        <div>
                                            <Form.Label className="fw-bold txt-subtle">
                                                {i18n.t("pages.ManagePermissions.search_user")}
                                            </Form.Label>
                                            <div style={{ position: "relative", zIndex: 100 }}>
                                                <AutocompleteSelect
                                                    type="users"
                                                    data={users}
                                                    onSelect={onRowSelect}
                                                    value={selectedUser}
                                                    placeholder={i18n.t("components.modal.users.search_user")}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <Form.Label className="fw-bold txt-subtle">
                                                {i18n.t("pages.ManagePermissions.search_role")}
                                            </Form.Label>
                                            <div style={{ position: "relative", zIndex: 100 }}>
                                                <AutocompleteSelect
                                                    type="roles"
                                                    data={roles}
                                                    onSelect={onRowSelect}
                                                    value={selectedRole}
                                                    placeholder={i18n.t("components.modal.roles.search_role")}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </Col>
                                <Col xs={12} md={8}>
                                    <Form.Label className="fw-bold txt-subtle">
                                        {i18n.t("pages.ManagePermissions.replicate_from")}
                                    </Form.Label>
                                    <Form.Select
                                        className="bg-surface txt-main shadow-none border-thin"
                                        value={replicateId}
                                        onChange={(e) => setReplicateId(e.target.value)}
                                    >
                                        <option value="">
                                            {i18n.t("pages.ManagePermissions.replicate_none")}
                                        </option>
                                        {(Array.isArray(items) ? items : []).map((entry) => {
                                            const info = getAssignmentInfo(entry);
                                            return (
                                                <option value={entry.id} key={entry.id}>
                                                    {info.label}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                </Col>
                                <Col xs={12} md={4} className="d-flex align-items-end">
                                    <button
                                        type="button"
                                        className="btn btn-secondary w-100 fw-bold"
                                        onClick={onReplicate}
                                    >
                                        {i18n.t("pages.ManagePermissions.replicate_apply")}
                                    </button>
                                </Col>
                            </Row>
                        ) : (
                            <div className="d-flex gap-3 align-items-center flex-wrap py-1">
                                <span className="txt-subtle fw-bold">
                                    {i18n.t("pages.ManagePermissions.assigned_to")}
                                </span>
                                <strong className="txt-main fs-5">{assignmentInfo.label}</strong>
                                <span
                                    className="fw-bold shadow-sm"
                                    style={{
                                        backgroundColor: assignmentInfo.type === "role" 
                                            ? "var(--success-green)" 
                                            : assignmentInfo.type === "user"
                                                ? "var(--action-color)"
                                                : "var(--brand-primary)",
                                        color: "var(--white)",
                                        padding: "5px 10px",
                                        borderRadius: "var(--btn-radius)",
                                        fontSize: "0.75rem",
                                        display: "inline-block",
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    {assignmentInfo.type === "user"
                                        ? i18n.t("pages.ManagePermissions.filter_user")
                                        : assignmentInfo.type === "role"
                                            ? i18n.t("pages.ManagePermissions.filter_role")
                                            : i18n.t("pages.ManagePermissions.filter_user_role")}
                                </span>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                <Card className="permissions-panel mb-3 border-0">
                    <Card.Body>
                        <h5 className="mb-2 fw-bold txt-main">
                            {i18n.t("pages.ManagePermissions.groups_title")}
                        </h5>
                        <p className="permissions-subtitle mb-4">
                            {i18n.t("pages.ManagePermissions.groups_subtitle")}
                        </p>
                        
                        {isLoading && !isCreate && !item ? (
                            <div className="d-flex align-items-center gap-2 txt-subtle fw-bold">
                                <Spinner size="sm" />
                                <span>{i18n.t("pages.ManagePermissions.loading")}</span>
                            </div>
                        ) : (
                            <div className="permissions-groups-wrap">
                                {groupedActionLabels.map((group) => (
                                    <section
                                        className="permissions-group-section shadow-sm"
                                        key={group.id}
                                    >
                                        <div className="permissions-group-header mb-3 pb-2" style={{ borderBottom: "var(--border-thin)" }}>
                                            <Form.Check
                                                type="switch"
                                                label={<span className="fw-bold txt-main fs-6">{group.title}</span>}
                                                id={`permission-group-${group.id}`}
                                                checked={permissionGroupChecked(group)}
                                                onChange={(e) => onPermissionGroupSwitchChange(group)(e)}
                                                className="mb-1"
                                            />
                                            {group.description ? (
                                                <p className="txt-subtle mb-0 ms-5" style={{ fontSize: "0.85rem", marginTop: "-4px" }}>
                                                    {group.description}
                                                </p>
                                            ) : null}
                                        </div>
                                        <div className="permissions-actions-grid">
                                            {group.actions.map((actionLabel) => (
                                                <Card
                                                    key={actionLabel.key}
                                                    className="permission-action-card border-thin bg-surface"
                                                    style={{ boxShadow: "none" }}
                                                >
                                                    <Card.Body className="p-3">
                                                        <Form.Check
                                                            type="switch"
                                                            id={`action-${actionLabel.key}`}
                                                            checked={Boolean(actionState[actionLabel.key])}
                                                            onChange={(e) => onSwitchChange(actionLabel)(e)}
                                                            label={<span className="txt-main fw-semibold">{actionLabel.title}</span>}
                                                        />
                                                        {actionLabel.description ? (
                                                            <small className="txt-subtle d-block ms-5" style={{ marginTop: "-2px" }}>
                                                                {actionLabel.description}
                                                            </small>
                                                        ) : null}
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </div>
                                    </section>
                                ))}
                            </div>
                        )}
                    </Card.Body>
                </Card>

                <div className="permissions-crud-actions mt-4 pt-3" style={{ borderTop: "var(--border-thin)" }}>
                    {!isCreate ? (
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={onDelete}
                            disabled={isLoading}
                        >
                            {i18n.t("common.actions.delete")}
                        </button>
                    ) : null}

                    <button type="submit" className="btn btn-primary px-4" disabled={isLoading}>
                        {isCreate
                            ? i18n.t("common.actions.create")
                            : i18n.t("common.actions.update")}
                    </button>
                </div>
            </Form>
        </Container>
    );
};

export default ActionsPermissionsCRUD;